/**
 * Trend/Record Visualization fixtures — mirrors components/trend-viz/types.ts
 * and stays consistent with fixtures/patient.ts (same values, same dates).
 */

import { conditions, diastolicSeries, DEMO_TODAY } from './patient';

const DIA_ID = 'vital:8462-4@mm[Hg]';

/** The five history windows, matching SmartHistoryWindow. */
const WINDOW_YEARS: Record<string, number | null> = {
  '1y': 1, '2y': 2, '5y': 5, '10y': 10, all: null,
};
const WINDOW_LABELS: Record<string, string> = {
  '1y': 'Last 1 year', '2y': 'Last 2 years', '5y': 'Last 5 years',
  '10y': 'Last 10 years', all: 'All data',
};

/**
 * The demo is windowed for real rather than always answering with everything:
 * the launch picker's choice reaches these endpoints, and a chart that ignored
 * it would quietly contradict the "2 YEARS" badge the header displays.
 */
export function resolveWindow(code: unknown) {
  const key = typeof code === 'string' && code in WINDOW_YEARS ? code : '2y';
  const years = WINDOW_YEARS[key];
  const start =
    years == null
      ? null
      : new Date(Date.UTC(DEMO_TODAY.getUTCFullYear() - years, DEMO_TODAY.getUTCMonth(), DEMO_TODAY.getUTCDate()))
          .toISOString()
          .slice(0, 10);
  return { window: { code: key, label: WINDOW_LABELS[key], years }, start };
}

/** True when a dated record falls inside the window (null start = all history). */
function inWindow(date: string | null, start: string | null): boolean {
  if (!date) return false;
  return start == null || date >= start;
}

/** Ids are derived from the concept's name, not its array position, so the
 *  chat's Trend-Viz hand-off targets keep resolving if fixtures are reordered. */
function slug(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 48);
}
export const conditionId = (title: string) => `condition:${slug(title)}`;

export function demoTrendInventory(windowCode?: unknown) {
  const { window, start } = resolveWindow(windowCode);
  const bp = diastolicSeries.filter((p) => inWindow(p.date, start));
  // A problem or a drug that began BEFORE the window is still active during it —
  // only a record that never overlaps the window drops out.
  const conds = conditions.filter((c) => c.status === 'active' || inWindow(c.onset, start));

  const categories = [
    {
      // Diastolic blood pressure is the one measurement the captured answer
      // reports, and the only series whose values we actually hold.
      id: 'vitals',
      label: 'Vital signs',
      count: bp.length ? 1 : 0,
      record_count: bp.length,
      items: bp.length
        ? [{
            id: DIA_ID,
            label: 'Diastolic Blood Pressure',
            type: 'vital',
            plot_class: 'numeric-series',
            unit: 'mm[Hg]',
            count: bp.length,
            date_span: { first: bp[0].date, last: bp[bp.length - 1].date },
          }]
        : [],
    },
    {
      id: 'conditions',
      label: 'Conditions',
      count: conds.length,
      record_count: conds.length,
      items: conds.map((c) => ({
        id: conditionId(c.title),
        label: c.title,
        type: 'condition',
        plot_class: 'condition-range',
        unit: null,
        count: 1,
        status: c.status,
        active: c.status === 'active',
        has_trajectory: c.status === 'active',
        date_span: { first: c.onset, last: c.resolved ?? null },
      })),
    },
  ];

  return { window, categories: categories.filter((c) => c.items.length > 0) };
}

export function demoTrendSeries(itemIds: string[], windowCode?: unknown) {
  const { window, start } = resolveWindow(windowCode);
  const wanted = new Set(itemIds || []);
  const numeric_series: unknown[] = [];
  const condition_ranges: unknown[] = [];
  const events: unknown[] = [];
  const course_ranges: unknown[] = [];

  if (wanted.has(DIA_ID)) {
    numeric_series.push({
      id: DIA_ID, label: 'Diastolic Blood Pressure', unit: 'mm[Hg]', type: 'vital',
      points: diastolicSeries.filter((p) => inWindow(p.date, start)).map((p) => ({ date: p.date, value: p.value })),
      source: 'demo-fixture', decimated: false,
    });
  }

  for (const c of conditions) {
    const id = conditionId(c.title);
    if (!wanted.has(id)) continue;
    condition_ranges.push({
      id,
      label: c.title,
      onset: c.onset,
      active: c.status === 'active',
      status: c.status,
      end: c.resolved ?? null,
      // The bar starts before the window opens, so the renderer must draw that
      // edge as inferred rather than asserting the problem began there.
      clipped_left: start != null && c.onset < start,
      recorded_count: 1,
      source: 'demo-fixture',
    });
  }

  return { window, numeric_series, condition_ranges, events, course_ranges };
}

export function demoTrendAlerts(windowCode?: unknown) {
  const { window, start } = resolveWindow(windowCode);
  const involved = diastolicSeries
    .filter((p) => p.value < 60 && inWindow(p.date, start))
    .map((p) => ({ date: p.date, value: p.value }));
  if (!involved.length) return { window, alerts: [], count: 0 };
  return {
    window,
    alerts: [
      {
        id: 'demo-alert-dbp-low',
        rule_id: 'sustained-below-reference',
        severity: 'moderate',
        series_id: DIA_ID,
        series_label: 'Diastolic Blood Pressure',
        detected_date: involved[involved.length - 1].date,
        sentence: `Diastolic blood pressure fell below 60 mm[Hg] on ${involved.length} readings, the most recent on ${involved[involved.length - 1].date}.`,
        detail: 'A widening pulse pressure with a falling diastolic is the expected pattern in aortic stenosis, and it also narrows the margin for coronary perfusion in known ischemic heart disease.',
        involved_points: involved,
      },
    ],
    count: 1,
  };
}
